import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type ClientType = "PERSONA_NATURAL" | "PERSONA_JURIDICA";
export type DocumentType = "CC" | "CE" | "NIT" | "PASAPORTE" | "TI";
export type ClientStatus = "ACTIVE" | "INACTIVE" | "PROSPECT" | "ARCHIVED";

export interface ClientContactRecord {
  id: string;
  fullName: string;
  position: string | null;
  phone: string | null;
  email: string | null;
  isPrimary: boolean;
}

export interface ClientRecord {
  id: string;
  clientType: ClientType;
  documentType: DocumentType;
  documentNumber: string;
  verificationDigit: string | null;
  fullName: string | null;
  businessName: string | null;
  tradeName: string | null;
  legalRepresentativeName: string | null;
  legalRepresentativeDocument: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string;
  economicActivity: string | null;
  ciiuCode: string | null;
  taxRegime: string | null;
  taxResponsibilities: string[];
  status: ClientStatus;
  tags: string[];
  notes: string | null;
  createdAt: string;
  contacts?: ClientContactRecord[];
  assignedTo?: { id: string; fullName: string; email: string } | null;
  /** Ubicación manual para el módulo "Operación" (mapa) — null si nunca se marcó. */
  latitude: number | string | null;
  longitude: number | string | null;
  /** Accesos a portales del Estado. La contraseña NUNCA viaja del backend — solo estos booleanos indican si ya hay una guardada (ver Client.toJSON() en el backend). */
  dianUsername: string | null;
  dianPasswordSet: boolean;
  camaraComercioUsername: string | null;
  camaraComercioPasswordSet: boolean;
  icaUsername: string | null;
  icaPasswordSet: boolean;
}

/** Solo para escritura (crear/actualizar cliente) — las contraseñas de portales nunca vienen del backend, así que no pueden salir de `Partial<ClientRecord>`. Dejar en blanco = no tocar la contraseña ya guardada (ver client.service.ts). */
export interface PortalCredentialsWritePayload {
  dianUsername?: string;
  dianPassword?: string;
  camaraComercioUsername?: string;
  camaraComercioPassword?: string;
  icaUsername?: string;
  icaPassword?: string;
}

export type PortalName = "DIAN" | "CAMARA_COMERCIO" | "ICA";
export const PORTAL_LABEL: Record<PortalName, string> = {
  DIAN: "DIAN",
  CAMARA_COMERCIO: "Cámara de Comercio",
  ICA: "ICA",
};

export interface ListClientsFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ClientStatus;
  clientType?: ClientType;
}

export function displayName(client: Pick<ClientRecord, "clientType" | "businessName" | "tradeName" | "fullName" | "documentNumber">) {
  if (client.clientType === "PERSONA_JURIDICA") return client.businessName || client.tradeName || client.documentNumber;
  return client.fullName || client.documentNumber;
}

export function useClients(filters: ListClientsFilters) {
  return useQuery({
    queryKey: ["clients", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientRecord[]>>("/clients", { params: filters });
      return data;
    },
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ["clients", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientRecord>>(`/clients/${id}`);
      return data.data;
    },
  });
}

export type CreateClientPayload = Partial<ClientRecord> &
  PortalCredentialsWritePayload & {
    clientType: ClientType;
    documentType: DocumentType;
    documentNumber: string;
  };

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateClientPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ClientRecord>>("/clients", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<ClientRecord> & PortalCredentialsWritePayload & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ClientRecord>>(`/clients/${id}`, payload);
      return data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", variables.id] });
    },
  });
}

/** Descifra y devuelve, bajo pedido explícito, la contraseña real de un portal (DIAN/Cámara de Comercio/ICA) guardada para un cliente — auditado en el backend (ver client.service.ts). */
export function useRevealPortalCredential() {
  return useMutation({
    mutationFn: async ({ clientId, portal }: { clientId: string; portal: PortalName }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<{ username: string | null; password: string | null }>>(
        `/clients/${clientId}/portal-credentials/reveal`,
        { portal }
      );
      return data.data;
    },
  });
}

/** Datos mínimos para el módulo "Operación" (mapa de clientes) — solo los que ya tienen coordenadas registradas manualmente. */
export interface ClientMapRecord {
  id: string;
  clientType: ClientType;
  fullName: string | null;
  businessName: string | null;
  tradeName: string | null;
  documentNumber: string;
  city: string | null;
  state: string | null;
  status: ClientStatus;
  taxResponsibilities: string[];
  latitude: number | string;
  longitude: number | string;
}

export function useClientsMap() {
  return useQuery({
    queryKey: ["clients", "map"],
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiSuccessResponse<{ clients: ClientMapRecord[]; totalClients: number; withLocation: number }>
      >("/clients/map");
      return data.data;
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/clients/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["clients"] }),
  });
}

export interface CreateContactPayload {
  clientId: string;
  fullName: string;
  position?: string;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

export function useAddContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, ...payload }: CreateContactPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ClientContactRecord>>(`/clients/${clientId}/contacts`, payload);
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId] }),
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, contactId }: { clientId: string; contactId: string }) => {
      await apiClient.delete(`/clients/${clientId}/contacts/${contactId}`);
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["clients", variables.clientId] }),
  });
}
